import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import { NotificationProvider } from './contexts/NotificationContext';
import { AlertTriangle } from 'lucide-react';

// 導入頁面
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import OrdersPage from './pages/orders/OrdersPage';
import OrderDetailPage from './pages/orders/OrderDetailPage';
import DriversPage from './pages/drivers/DriversPage';
import DriverDetailPage from './pages/drivers/DriverDetailPage';
import UsersPage from './pages/users/UsersPage';
import UserDetailPage from './pages/users/UserDetailPage';
import UserFormPage from './pages/users/UserFormPage';
import DispatchersPage from './pages/dispatchers/DispatchersPage';
import DispatcherDetailPage from './pages/dispatchers/DispatcherDetailPage';
import DispatcherFormPage from './pages/dispatchers/DispatcherFormPage';
import FleetsPage from './pages/fleets/FleetsPage';
import FleetDetailPage from './pages/fleets/FleetDetailPage';
import ReportsPage from './pages/reports/ReportsPage';
import DailyReportsPage from './pages/reports/DailyReportsPage';
import DriverReportsPage from './pages/reports/DriverReportsPage';
import RevenueReportsPage from './pages/reports/RevenueReportsPage';
import SettingsPage from './pages/settings/SettingsPage';
import MessagesPage from './pages/messages/MessagesPage';

// 佈局
import MainLayout from './components/layout/MainLayout';
import LogsPage from './pages/logs/LogsPage';
import FleetComparisonReportsPage from './pages/reports/FleetComparisonReportsPage';

// 受保護的路由
const ProtectedRoute = ({
    children,
    requiredPermissions = [],
    fallbackRoute = '/dashboard'
}) => {
    const { user, loading, logout } = useAuth();

    // 加載狀態
    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    // 未登錄，重定向到登錄頁
    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // 檢查權限
    const hasRequiredPermissions = requiredPermissions.length === 0 ||
        requiredPermissions.some(perm =>
            user.permissions?.includes(perm) ||
            user.user_type === 'admin'
        );

    // 沒有權限
    if (!hasRequiredPermissions) {
        return (
            <div className="h-screen flex items-center justify-center bg-gray-100">
                <div className="text-center p-8 bg-white rounded-lg shadow-md">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertTriangle className="w-8 h-8 text-red-500" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 mb-2">無訪問權限</h2>
                    <p className="text-gray-600 mb-4">您沒有權限訪問此頁面。</p>
                    <button
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                        onClick={logout}
                    >
                        返回登入頁面
                    </button>
                </div>
            </div>
        );
    }

    // 有權限，渲染子組件
    return children;
};

const App = () => {
    return (
        <AuthProvider>
            <NotificationProvider>
                <Router>
                    <Routes>
                        <Route path="/login" element={<LoginPage />} />

                        {/* 儀表板 */}
                        <Route path="/" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <DashboardPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/dashboard" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <DashboardPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />

                        {/* 訂單管理 */}
                        <Route path="/orders" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <OrdersPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/orders/:orderId" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <OrderDetailPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />

                        {/* 司機管理 */}
                        <Route path="/drivers" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <DriversPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/drivers/:driverId" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <DriverDetailPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />

                        {/* 用戶管理 */}
                        <Route path="/users" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <UsersPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/users/new" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <UserFormPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/users/:userId" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <UserDetailPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/users/:userId/edit" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <UserFormPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />

                        {/* 調度員管理 */}
                        <Route path="/dispatchers" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <DispatchersPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/dispatchers/new" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <DispatcherFormPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/dispatchers/:dispatcherId" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <DispatcherDetailPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/dispatchers/:dispatcherId/edit" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <DispatcherFormPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />

                        {/* 車隊管理 */}
                        <Route path="/fleets" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <FleetsPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/fleets/:fleetId" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <FleetDetailPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />

                        {/* 消息管理 */}
                        <Route path="/messages" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <MessagesPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />

                        {/* 報表 */}
                        <Route path="/reports" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <ReportsPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/reports/daily" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <DailyReportsPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/reports/drivers" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <DriverReportsPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/reports/revenue" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <RevenueReportsPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/reports/fleets" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <FleetComparisonReportsPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />

                        {/* 系統設置 */}
                        <Route path="/settings" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <SettingsPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />

                        {/* 操作日誌 */}
                        <Route path="/logs" element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <LogsPage />
                                </MainLayout>
                            </ProtectedRoute>
                        } />

                        {/* 捕捉所有其他路徑 - 重定向到儀表板 */}
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                </Router>
            </NotificationProvider>
        </AuthProvider>
    );
};

export default App;